import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, Clock, CheckCircle, Edit, Eye, Link2, Globe } from 'lucide-react';

export default function SHNDPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await api.get('/shnd');
      setApplications(res.data.applications || []);
    } catch (err) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await api.post('/shnd', { status: 'draft', current_step: 1 });
      toast.success('Application created');
      navigate(`/shnd/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create application');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/shnd/${deleteId}`);
      toast.success('Application deleted');
      setDeleteId(null);
      fetchApplications();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const statusBadge = (status) => {
    if (status === 'completed') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Completed</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Draft</Badge>;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shnd-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SHND Applications</h1>
          <p className="text-sm text-slate-500 mt-1">Application & Counselling Forms</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-shnd-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Application
        </Button>
      </div>

      {/* Public Link Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">Public Form Link</p>
              <p className="text-xs text-blue-600">{window.location.origin}/shnd/public</p>
            </div>
          </div>
          <Button
            variant="outline" size="sm"
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
            data-testid="copy-public-link"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/shnd/public`);
              toast.success('Public link copied!');
            }}
          >
            <Link2 className="w-4 h-4 mr-1" /> Copy Link
          </Button>
        </CardContent>
      </Card>

      {applications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">No applications yet</p>
            <p className="text-slate-400 text-sm mt-1">Create your first SHND application</p>
            <Button className="mt-4" onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {applications.map((app) => (
            <Card key={app.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`shnd-card-${app.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0" onClick={() => navigate(`/shnd/${app.id}`)}>
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {app.personal_details?.name || 'Untitled Application'}
                      </h3>
                      {statusBadge(app.status)}
                      {app.source === 'public' && <Badge className="bg-blue-100 text-blue-700 border-blue-200">Public</Badge>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {app.id}
                      </span>
                      {app.personal_details?.its && (
                        <span>ITS: {app.personal_details.its}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(app.created_at)}
                      </span>
                      {app.status === 'draft' && (
                        <span className="text-amber-600">Step {app.current_step || 1}/6</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline" size="sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/shnd/${app.id}`); }}
                      data-testid={`edit-shnd-${app.id}`}
                    >
                      {app.status === 'completed' ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={(e) => { e.stopPropagation(); setDeleteId(app.id); }}
                      data-testid={`delete-shnd-${app.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Application?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
